import React from "react";
import { Badge } from "../../ui";
import { FeatherAlertCircle } from "@subframe/core";
import { DepositFromOtherAccountsSplitCategoryDetails } from "../../services/api";

interface CustomerDepositFromOtherAccountsSplitViewContentProps {
  details: DepositFromOtherAccountsSplitCategoryDetails | null;
  isLoading: boolean;
}

const formatDate = (dateString?: string): string => {
  if (!dateString) return "—";
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-IN", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return dateString;
  }
};

const formatCurrency = (amount: number): string => {
  return `₹${amount.toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

const CustomerDepositFromOtherAccountsSplitViewContent: React.FC<
  CustomerDepositFromOtherAccountsSplitViewContentProps
> = ({ details, isLoading }) => {
  const hasDetails = Boolean(details);

  return (
    <div className="flex w-full flex-col gap-4 rounded-md border border-solid border-neutral-border bg-neutral-50 px-6 py-6">
      <div className="flex w-full items-center justify-between">
        <div className="flex flex-wrap items-center gap-3">
          <span className="text-heading-3 font-heading-3 text-default-font">
            Deposit From Other Accounts (Split)
          </span>
          <Badge variant="neutral">Deposit Split</Badge>
        </div>
      </div>

      {isLoading ? (
        <div className="flex w-full items-center justify-center py-12 text-body text-subtext-color">
          Loading transaction details...
        </div>
      ) : !hasDetails ? (
        <div className="flex w-full flex-col items-center gap-2 rounded border border-dashed border-neutral-border px-4 py-6 text-center text-body text-subtext-color">
          <FeatherAlertCircle className="text-neutral-400" />
          <span>No deposit split details available for this transaction.</span>
        </div>
      ) : (
        <>
          {/* Global Information */}
          <div className="grid gap-4 md:grid-cols-2">
            <div className="flex flex-col rounded border border-neutral-border bg-white px-4 py-3">
              <span className="text-caption text-subtext-color">Date</span>
              <span className="text-body-bold text-default-font">
                {formatDate(details?.date)}
              </span>
            </div>
            <div className="flex flex-col rounded border border-neutral-border bg-white px-4 py-3">
              <span className="text-caption text-subtext-color">
                Received From
              </span>
              <span className="text-body-bold text-default-font">
                {details?.customer_name || "—"}
              </span>
            </div>
            <div className="flex flex-col rounded border border-neutral-border bg-white px-4 py-3">
              <span className="text-caption text-subtext-color">
                Reference Number
              </span>
              <span className="text-body-bold text-default-font">
                {details?.reference_number || "—"}
              </span>
            </div>
            <div className="flex flex-col rounded border border-neutral-border bg-white px-4 py-3">
              <span className="text-caption text-subtext-color">
                Total Amount
              </span>
              <span className="text-body-bold text-default-font">
                {details?.total_amount
                  ? formatCurrency(details.total_amount)
                  : "—"}
              </span>
            </div>
          </div>

          {/* Splits Table */}
          {details?.splits && details.splits.length > 0 && (
            <div className="flex w-full flex-col gap-3">
              <div className="flex items-center justify-between">
                <span className="text-heading-4 font-heading-4 text-default-font">
                  Split Payments ({details.split_count || details.splits.length})
                </span>
              </div>
              <div className="overflow-x-auto rounded border border-neutral-border bg-white">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-neutral-border bg-neutral-50">
                      <th className="px-4 py-3 text-left text-caption font-body-bold text-subtext-color">
                        From Account
                      </th>
                      <th className="px-4 py-3 text-right text-caption font-body-bold text-subtext-color">
                        Amount
                      </th>
                      <th className="px-4 py-3 text-left text-caption font-body-bold text-subtext-color">
                        Received Via
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {details.splits?.map((split, index) => (
                      <tr
                        key={`${split.from_account_id}-${index}`}
                        className="border-b border-neutral-border last:border-b-0"
                      >
                        <td className="px-4 py-3 text-body text-default-font">
                          {split.from_account_name || "—"}
                        </td>
                        <td className="px-4 py-3 text-right text-body-bold text-default-font">
                          {formatCurrency(split.amount)}
                        </td>
                        <td className="px-4 py-3 text-body text-default-font">
                          {split.payment_mode || "—"}
                        </td>
                      </tr>
                    ))}
                    <tr className="bg-neutral-50 font-body-bold">
                      <td className="px-4 py-3 text-body-bold text-default-font">
                        Total
                      </td>
                      <td className="px-4 py-3 text-right text-body-bold text-default-font">
                        {details.total_amount
                          ? formatCurrency(details.total_amount)
                          : "—"}
                      </td>
                      <td className="px-4 py-3"></td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default CustomerDepositFromOtherAccountsSplitViewContent;
